import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import { Loader } from "@googlemaps/js-api-loader";
import config from "../Config/config";

const GOOGLE_MAPS_API_KEY = String(
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyDjAbPhawP8SIJwGHX-W5XonP5U_vOUqAM"
).trim();

const normalizeText = (v) => String(v || "").replace(/\s+/g, " ").trim();
const uniqSortedOptions = (values = []) =>
  Array.from(new Set((Array.isArray(values) ? values : []).map((value) => normalizeText(value)).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );

function LocationForm({
  header,
  initial,
  saving = false,
  onSubmit,
  onCancel,
}) {
  const initialName = normalizeText(initial?.name || "");
  const initialAddress = normalizeText(initial?.address || "");
  const [name, setName] = useState(initialName);
  const [address, setAddress] = useState(initialAddress);
  const [searchAddress, setSearchAddress] = useState(initialAddress);
  const [cityOptions, setCityOptions] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [cityOptionsLoading, setCityOptionsLoading] = useState(false);
  const [addressOptionsLoading, setAddressOptionsLoading] = useState(false);
  const [gmapsReady, setGmapsReady] = useState(false);
  const [gmapsError, setGmapsError] = useState("");
  const autocompleteServiceRef = useRef(null);
  const cityOptionsTimerRef = useRef(null);
  const addressOptionsTimerRef = useRef(null);
  const cityOptionsSeqRef = useRef(0);
  const addressOptionsSeqRef = useRef(0);

  useEffect(() => {
    setName(initialName);
    setAddress(initialAddress);
    setSearchAddress(initialAddress);
    setCityOptions(initialName ? [initialName] : []);
    setAddressOptions(initialAddress ? [initialAddress] : []);
  }, [initialAddress, initialName]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (window.google?.maps?.places) {
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
          if (!cancelled) {
            setGmapsReady(true);
            setGmapsError("");
          }
          return;
        }
        if (!GOOGLE_MAPS_API_KEY) {
          if (!cancelled) {
            setGmapsReady(false);
            setGmapsError("Google city and address search are unavailable right now.");
          }
          return;
        }
        const loader = new Loader({
          apiKey: GOOGLE_MAPS_API_KEY,
          version: "weekly",
          libraries: ["places"],
        });
        await loader.load();
        if (cancelled || !window.google?.maps?.places) return;
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        setGmapsReady(true);
        setGmapsError("");
      } catch (err) {
        if (cancelled) return;
        setGmapsReady(false);
        setGmapsError("Google city and address search are unavailable right now.");
      }
    })();

    return () => {
      cancelled = true;
      if (cityOptionsTimerRef.current) clearTimeout(cityOptionsTimerRef.current);
      if (addressOptionsTimerRef.current) clearTimeout(addressOptionsTimerRef.current);
    };
  }, []);

  const fetchPredictions = async (inputValue = "", request = {}) => {
    const query = normalizeText(inputValue);
    if (!autocompleteServiceRef.current || query.length < 2) return [];

    return new Promise((resolve) => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: query,
          ...request,
        },
        (predictions, status) => {
          const ok = status === window.google?.maps?.places?.PlacesServiceStatus?.OK;
          if (!ok || !Array.isArray(predictions)) {
            resolve([]);
            return;
          }
          resolve(
            uniqSortedOptions(
              predictions.map((prediction) => String(prediction?.description || "").trim())
            )
          );
        }
      );
    });
  };

  const queueCityOptions = (inputValue = "") => {
    const query = normalizeText(inputValue);
    if (!gmapsReady || query.length < 2) {
      if (cityOptionsTimerRef.current) clearTimeout(cityOptionsTimerRef.current);
      setCityOptions(query ? [query] : []);
      setCityOptionsLoading(false);
      return;
    }
    const seq = ++cityOptionsSeqRef.current;
    if (cityOptionsTimerRef.current) clearTimeout(cityOptionsTimerRef.current);
    setCityOptionsLoading(true);
    cityOptionsTimerRef.current = setTimeout(async () => {
      const options = await fetchPredictions(query, {
        types: ["(cities)"],
        componentRestrictions: { country: "us" },
      });
      if (seq !== cityOptionsSeqRef.current) return;
      setCityOptions(uniqSortedOptions([query, ...options]));
      setCityOptionsLoading(false);
    }, 220);
  };

  const queueAddressOptions = (inputValue = "") => {
    const query = normalizeText(inputValue);
    if (!gmapsReady || query.length < 2) {
      if (addressOptionsTimerRef.current) clearTimeout(addressOptionsTimerRef.current);
      setAddressOptions(query ? [query] : []);
      setAddressOptionsLoading(false);
      return;
    }
    const seq = ++addressOptionsSeqRef.current;
    if (addressOptionsTimerRef.current) clearTimeout(addressOptionsTimerRef.current);
    setAddressOptionsLoading(true);
    addressOptionsTimerRef.current = setTimeout(async () => {
      const options = await fetchPredictions(query, {
        types: ["address"],
      });
      if (seq !== addressOptionsSeqRef.current) return;
      setAddressOptions(uniqSortedOptions([query, ...options]));
      setAddressOptionsLoading(false);
    }, 220);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.({
      name: normalizeText(name),
      address: normalizeText(address),
    });
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid rgba(148,163,184,0.28)",
        background: "#fff",
      }}
    >
      <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1.25 }}>{header}</Typography>
      <Box sx={{ display: "grid", gap: 1.1 }}>
        <Autocomplete
          freeSolo
          size="small"
          options={cityOptions}
          value={name}
          inputValue={name}
          loading={gmapsReady && cityOptionsLoading}
          filterOptions={(options) => options}
          onInputChange={(_, value, reason) => {
            const next = String(value || "");
            setName(next);
            if (reason === "input") {
              queueCityOptions(next);
            } else if (reason === "clear") {
              setCityOptions([]);
            }
          }}
          onChange={(_, value) => {
            const next = String(value || "");
            setName(next);
            queueCityOptions(next);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Location Name"
              placeholder={gmapsReady ? "Search and select a city" : "Type a city"}
              required
              fullWidth
            />
          )}
        />
        <Autocomplete
          freeSolo
          size="small"
          options={addressOptions}
          value={searchAddress}
          inputValue={searchAddress}
          loading={gmapsReady && addressOptionsLoading}
          filterOptions={(options) => options}
          onInputChange={(_, value, reason) => {
            const next = String(value || "");
            setSearchAddress(next);
            setAddress(next);
            if (reason === "input") {
              queueAddressOptions(next);
            } else if (reason === "clear") {
              setAddressOptions([]);
            }
          }}
          onChange={(_, value) => {
            const next = String(value || "");
            setSearchAddress(next);
            setAddress(next);
            queueAddressOptions(next);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Address"
              placeholder={gmapsReady ? "Search and select an address" : "Type an address"}
              fullWidth
            />
          )}
        />
        <TextField
          label="Actual Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Full street address"
          required
          size="small"
          fullWidth
          multiline
          minRows={2}
        />
        {gmapsError ? <Alert severity="warning" sx={{ borderRadius: 2 }}>{gmapsError}</Alert> : null}
        {!gmapsError ? (
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            Location name now uses Google city search, and Search Address uses Google address predictions to fill the actual address.
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1.5 }}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            fontWeight: 800,
            background: "linear-gradient(90deg,#2563eb,#1d4ed8)",
            "&:hover": { background: "linear-gradient(90deg,#1d4ed8,#1e40af)" },
          }}
        >
          {saving ? "Saving..." : "Save Location"}
        </Button>
      </Box>
    </Paper>
  );
}

export default function Location({
  subscriptionId: subscriptionIdProp = "",
  userId: userIdProp = "",
  onLocationsChanged,
}) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState({ mode: "", locationId: "" });
  const userId = normalizeText(userIdProp || localStorage.getItem("userId") || "");
  const [subscriptionId, setSubscriptionId] = useState(normalizeText(subscriptionIdProp || ""));
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    const nextSubscriptionId = normalizeText(subscriptionIdProp || "");
    if (nextSubscriptionId) {
      setSubscriptionId(nextSubscriptionId);
    }
  }, [subscriptionIdProp]);

  useEffect(() => {
    if (subscriptionIdProp || !userId) return undefined;
    let cancelled = false;
    setSubscriptionLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/get-user-details`, {
          params: { userId },
        });
        if (cancelled) return;
        setSubscriptionId(normalizeText(data?.subscriptionId || ""));
      } catch {
        if (cancelled) return;
        setSubscriptionId("");
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subscriptionIdProp, userId]);

  const locationRequestConfig = useMemo(() => {
    const headers = {};
    const params = {};
    if (userId) {
      headers["x-user-id"] = userId;
      params.userId = userId;
    }
    if (subscriptionId) {
      headers["x-subscription-id"] = subscriptionId;
      params.subscriptionId = subscriptionId;
    }
    return { headers, params };
  }, [subscriptionId, userId]);

  const fetchLocations = async () => {
    if (!subscriptionId) {
      setLocations([]);
      onLocationsChanged?.([]);
      setError("");
      return [];
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/alllocation`, {
        ...locationRequestConfig,
      });
      const rows = Array.isArray(data) ? data : [];
      setLocations(rows);
      onLocationsChanged?.(rows);
      setError("");
      return rows;
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load locations.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLocations();
  }, [subscriptionId, userId]);

  const sortedLocations = useMemo(
    () =>
      [...(Array.isArray(locations) ? locations : [])].sort((a, b) =>
        normalizeText(a?.name || "").localeCompare(normalizeText(b?.name || ""))
      ),
    [locations]
  );

  const editingLocation = useMemo(
    () => sortedLocations.find((loc) => String(loc?.id || "") === String(active.locationId || "")) || null,
    [sortedLocations, active.locationId]
  );

  const handleCreate = async (payload) => {
    if (!subscriptionId) {
      setError("HiBid subscriptionId is missing. Link your subscriptionId and try again.");
      return;
    }
    setSaving(true);
    try {
      setError("");
      await axios.post(`${config.API_BASE_URL}/createlocation`, {
        userId,
        subscriptionId,
        name: payload?.name || "",
        address: payload?.address || "",
      }, {
        headers: locationRequestConfig.headers,
      });
      setActive({ mode: "", locationId: "" });
      await fetchLocations();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create location.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!editingLocation?.id) return;
    if (!subscriptionId) {
      setError("HiBid subscriptionId is missing. Link your subscriptionId and try again.");
      return;
    }
    setSaving(true);
    try {
      setError("");
      await axios.patch(`${config.API_BASE_URL}/updatelocation`, {
        userId,
        subscriptionId,
        locationId: editingLocation.id,
        name: payload?.name || "",
        address: payload?.address || "",
      }, {
        headers: locationRequestConfig.headers,
      });
      setActive({ mode: "", locationId: "" });
      await fetchLocations();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update location.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!locationId) return;
    if (!subscriptionId) {
      setError("HiBid subscriptionId is missing. Link your subscriptionId and try again.");
      return;
    }
    const ok = window.confirm("Delete this location?");
    if (!ok) return;
    try {
      setError("");
      await axios.delete(`${config.API_BASE_URL}/deletelocation`, {
        headers: locationRequestConfig.headers,
        data: { userId, subscriptionId, locationId, promoteChildren: false },
      });
      if (String(active.locationId || "") === String(locationId)) {
        setActive({ mode: "", locationId: "" });
      }
      await fetchLocations();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete location.");
    }
  };

  return (
    <Box sx={{ p: 2.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 22 }}>Location Metadata</Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
            Save reusable pickup locations with their exact addresses.
          </Typography>
        </Box>
        <Button
          disabled={!subscriptionId || subscriptionLoading}
          onClick={() => setActive({ mode: "create", locationId: "" })}
          startIcon={<AddLocationAltOutlinedIcon />}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 2,
            fontWeight: 900,
            color: "#fff",
            background: "linear-gradient(90deg,#16a34a,#15803d)",
            "&:hover": { background: "linear-gradient(90deg,#15803d,#166534)" },
          }}
        >
          Add Location
        </Button>
      </Box>

      {!subscriptionLoading && !subscriptionId ? (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
          HiBid subscriptionId is missing in this session, so location metadata cannot be saved for this org right now.
        </Alert>
      ) : null}

      {error ? <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert> : null}

      {active.mode === "create" ? (
        <Box sx={{ mt: 2 }}>
          <LocationForm
            header="Add Location"
            saving={saving}
            onSubmit={handleCreate}
            onCancel={() => setActive({ mode: "", locationId: "" })}
          />
        </Box>
      ) : null}

      {active.mode === "edit" && editingLocation ? (
        <Box sx={{ mt: 2 }}>
          <LocationForm
            header={`Edit ${editingLocation?.name || "Location"}`}
            initial={editingLocation}
            saving={saving}
            onSubmit={handleUpdate}
            onCancel={() => setActive({ mode: "", locationId: "" })}
          />
        </Box>
      ) : null}

      <Box sx={{ mt: 2, display: "grid", gap: 1.1 }}>
        {loading ? (
          <Paper sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Paper>
        ) : sortedLocations.length ? (
          sortedLocations.map((loc) => (
            <Paper
              key={loc.id}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "#fff",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1.1, flex: 1, minWidth: 0 }}>
                  <PlaceOutlinedIcon sx={{ color: "#4f46e5", mt: 0.1 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                      {normalizeText(loc?.name || "Unnamed Location")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#475569", mt: 0.25 }}>
                      {normalizeText(loc?.address || "No address saved")}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <IconButton size="small" onClick={() => setActive({ mode: "edit", locationId: loc.id })}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(loc.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))
        ) : (
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px dashed rgba(148,163,184,0.55)", textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: "#111827" }}>No locations saved yet</Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.4 }}>
              Add reusable pickup locations and save their exact addresses here.
            </Typography>
          </Paper>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" sx={{ color: "#64748b" }}>
        Auctions can use these saved location names while still showing the full address for staff reference.
      </Typography>
    </Box>
  );
}
